/**
 * PositionInputModal.js
 *
 * Big-button position input modal with GPS auto-fetch and manual entry.
 * Manual entry uses DD° MM.MMM' format with N/S E/W hemisphere buttons.
 * Designed for glove operation with 56px minimum tap targets.
 *
 * FT-284: now hosts <InlineTimeField> above the action row. Submits pass
 * (positionData, timeOptions) up to the parent. On boundary error the modal
 * stays open, renders <ModalErrorBanner>, and lets the Master re-pick a time
 * with an override reason without dismissing the modal.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, MapPin, Navigation, Loader2, AlertCircle, Crosshair } from 'lucide-react';
import { useInlineEventTime } from './useInlineEventTime';
import InlineTimeField from './InlineTimeField';
import ModalErrorBanner from './ModalErrorBanner';

// Manual entry reasons (for audit trail)
const MANUAL_REASONS = [
  { value: 'gps_failure', label: 'GPS not available' },
  { value: 'gps_inaccurate', label: 'GPS showing wrong position' },
  { value: 'verification', label: 'Verifying chart position' },
  { value: 'other', label: 'Other' }
];

const PositionInputModal = ({
  isOpen,
  onClose,
  onSubmit,
  vesselTimezone
}) => {
  // FT-284: UNCONDITIONAL hook calls — Rules-of-Hooks safe.
  const timeField = useInlineEventTime(vesselTimezone);

  // GPS state
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState(null);
  const [gpsPosition, setGpsPosition] = useState(null);

  // Manual entry state
  const [showManual, setShowManual] = useState(false);
  const [latDeg, setLatDeg] = useState('');
  const [latMin, setLatMin] = useState('');
  const [latHemi, setLatHemi] = useState('N');
  const [lonDeg, setLonDeg] = useState('');
  const [lonMin, setLonMin] = useState('');
  const [lonHemi, setLonHemi] = useState('W');
  const [manualReason, setManualReason] = useState('');
  const [manualNotes, setManualNotes] = useState('');

  // FT-284: boundary error + submit-in-flight guard.
  const [boundaryError, setBoundaryError] = useState(null);
  const submitInProgressRef = useRef(false);

  const resetState = useCallback(() => {
    setGpsLoading(false);
    setGpsError(null);
    setGpsPosition(null);
    setShowManual(false);
    setLatDeg('');
    setLatMin('');
    setLatHemi('N');
    setLonDeg('');
    setLonMin('');
    setLonHemi('W');
    setManualReason('');
    setManualNotes('');
    setBoundaryError(null);
  }, []);

  const attemptGps = useCallback(() => {
    if (!navigator.geolocation) {
      setGpsError('GPS not supported on this device');
      return;
    }

    setGpsLoading(true);
    setGpsError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGpsPosition({
          lat: position.coords.latitude,
          lon: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
        setGpsLoading(false);
      },
      (error) => {
        setGpsError(error.message || 'Could not get GPS position');
        setGpsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  // Try GPS on modal open
  useEffect(() => {
    if (isOpen) {
      resetState();
      // FT-284: also reset the time field when the modal opens.
      timeField.reset();
      attemptGps();
    }
    // timeField.reset is a stable useCallback; safe to omit from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, resetState, attemptGps]);

  if (!isOpen) return null;

  // Format decimal degrees to DD° MM.MMM'
  const formatCoordinate = (decimal, isLat) => {
    const abs = Math.abs(decimal);
    const deg = Math.floor(abs);
    const min = ((abs - deg) * 60).toFixed(3);
    const hemi = isLat ? (decimal >= 0 ? 'N' : 'S') : (decimal >= 0 ? 'E' : 'W');
    return `${deg}° ${min}' ${hemi}`;
  };

  // Convert manual entry to decimal
  const manualToDecimal = (deg, min, hemi) => {
    const decimal = parseFloat(deg) + (parseFloat(min) / 60);
    return (hemi === 'S' || hemi === 'W') ? -decimal : decimal;
  };

  const handleGpsSubmit = async () => {
    if (!gpsPosition || !timeField.isValid || submitInProgressRef.current) return;
    submitInProgressRef.current = true;
    setBoundaryError(null);
    try {
      const timeOptions = timeField.freshSnapshot();
      const result = await onSubmit(
        {
          lat: gpsPosition.lat,
          lon: gpsPosition.lon,
          accuracy: gpsPosition.accuracy,
          source: 'gps',
          timestamp: new Date().toISOString()
        },
        timeOptions
      );
      if (result?.boundaryError) {
        setBoundaryError(result.boundaryError);
        return;
      }
      onClose();
    } finally {
      submitInProgressRef.current = false;
    }
  };

  const handleManualSubmit = async () => {
    if (!latDeg || !latMin || !lonDeg || !lonMin || !manualReason) return;
    if (!timeField.isValid || submitInProgressRef.current) return;

    submitInProgressRef.current = true;
    setBoundaryError(null);
    try {
      const lat = manualToDecimal(latDeg, latMin, latHemi);
      const lon = manualToDecimal(lonDeg, lonMin, lonHemi);

      const timeOptions = timeField.freshSnapshot();
      const result = await onSubmit(
        {
          lat,
          lon,
          accuracy: null,
          source: 'manual',
          manualReason,
          manualNotes: manualNotes.trim() || null,
          timestamp: new Date().toISOString()
        },
        timeOptions
      );
      if (result?.boundaryError) {
        setBoundaryError(result.boundaryError);
        return;
      }
      onClose();
    } finally {
      submitInProgressRef.current = false;
    }
  };

  const canSubmitManual = latDeg && latMin && lonDeg && lonMin && manualReason && timeField.isValid;
  const canSubmitGps = !!gpsPosition && timeField.isValid;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-gray-900">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-cyan-500 rounded-lg">
              <MapPin className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Log Position</h2>
              <p className="text-sm text-slate-300">
                {showManual ? 'Manual entry' : 'GPS position'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-700 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-slate-300" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto flex-1 space-y-4">
          {/* FT-284: boundary error banner above the time field. */}
          {boundaryError && (
            <ModalErrorBanner message={boundaryError.humanMessage} />
          )}

          {/* FT-284: inline event-time field replaces the old TimePickerModal step. */}
          <InlineTimeField
            timeField={timeField}
            vesselTimezone={vesselTimezone}
          />

          {!showManual ? (
            /* GPS Mode */
            <>
              {/* GPS Status */}
              <div className={`p-4 rounded-xl border-2 ${
                gpsPosition ? 'bg-green-50 border-green-200' :
                gpsError ? 'bg-red-50 border-red-200' :
                'bg-slate-50 border-slate-200'
              }`}>
                {gpsLoading ? (
                  <div className="flex items-center justify-center gap-3 py-4">
                    <Loader2 className="h-6 w-6 text-cyan-600 animate-spin" />
                    <span className="text-slate-600">Getting GPS position...</span>
                  </div>
                ) : gpsPosition ? (
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 text-green-600 mb-2">
                      <Crosshair className="h-5 w-5" />
                      <span className="font-medium">Position found</span>
                    </div>
                    <p className="text-lg font-mono font-bold text-slate-800">
                      {formatCoordinate(gpsPosition.lat, true)}
                    </p>
                    <p className="text-lg font-mono font-bold text-slate-800">
                      {formatCoordinate(gpsPosition.lon, false)}
                    </p>
                    <p className="text-xs text-slate-500 mt-2">
                      Accuracy: ±{Math.round(gpsPosition.accuracy)}m
                    </p>
                  </div>
                ) : gpsError ? (
                  <div className="flex items-center gap-3 text-red-600">
                    <AlertCircle className="h-5 w-5 flex-shrink-0" />
                    <div>
                      <p className="font-medium">GPS Error</p>
                      <p className="text-sm text-red-500">{gpsError}</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <Navigation className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-slate-500">Ready to get position</p>
                  </div>
                )}
              </div>

              {/* GPS Actions */}
              <div className="space-y-3">
                {gpsPosition ? (
                  <button
                    onClick={handleGpsSubmit}
                    disabled={!canSubmitGps}
                    className="w-full py-4 min-h-[56px] bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    <Crosshair className="h-5 w-5" />
                    Log This Position
                  </button>
                ) : (
                  <button
                    onClick={attemptGps}
                    disabled={gpsLoading}
                    className="w-full py-4 min-h-[56px] bg-cyan-600 text-white rounded-lg font-medium hover:bg-cyan-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                  >
                    {gpsLoading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Navigation className="h-5 w-5" />
                    )}
                    {gpsLoading ? 'Getting Position...' : 'Get GPS Position'}
                  </button>
                )}

                <button
                  onClick={() => setShowManual(true)}
                  className="w-full py-3 min-h-[48px] border border-slate-300 text-slate-600 rounded-lg font-medium hover:bg-slate-50 transition-colors"
                >
                  Enter position manually
                </button>
              </div>
            </>
          ) : (
            /* Manual Entry Mode */
            <>
              {/* Latitude */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Latitude
                </label>
                <div className="flex gap-2 items-center">
                  <input
                    type="number"
                    value={latDeg}
                    onChange={(e) => setLatDeg(e.target.value)}
                    placeholder="DD"
                    min="0"
                    max="90"
                    className="w-20 px-3 py-3 min-h-[56px] border border-slate-300 rounded-lg text-lg text-center"
                  />
                  <span className="text-slate-500">°</span>
                  <input
                    type="number"
                    value={latMin}
                    onChange={(e) => setLatMin(e.target.value)}
                    placeholder="MM.MMM"
                    min="0"
                    max="59.999"
                    step="0.001"
                    className="flex-1 px-3 py-3 min-h-[56px] border border-slate-300 rounded-lg text-lg text-center"
                  />
                  <span className="text-slate-500">'</span>
                  <div className="flex gap-1">
                    {['N', 'S'].map(h => (
                      <button
                        key={h}
                        onClick={() => setLatHemi(h)}
                        className={`w-12 min-h-[56px] rounded-lg border-2 font-bold transition-all ${
                          latHemi === h
                            ? 'bg-cyan-600 border-cyan-700 text-white ring-4 ring-cyan-300'
                            : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {h}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Longitude */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Longitude
                </label>
                <div className="flex gap-2 items-center">
                  <input
                    type="number"
                    value={lonDeg}
                    onChange={(e) => setLonDeg(e.target.value)}
                    placeholder="DDD"
                    min="0"
                    max="180"
                    className="w-20 px-3 py-3 min-h-[56px] border border-slate-300 rounded-lg text-lg text-center"
                  />
                  <span className="text-slate-500">°</span>
                  <input
                    type="number"
                    value={lonMin}
                    onChange={(e) => setLonMin(e.target.value)}
                    placeholder="MM.MMM"
                    min="0"
                    max="59.999"
                    step="0.001"
                    className="flex-1 px-3 py-3 min-h-[56px] border border-slate-300 rounded-lg text-lg text-center"
                  />
                  <span className="text-slate-500">'</span>
                  <div className="flex gap-1">
                    {['E', 'W'].map(h => (
                      <button
                        key={h}
                        onClick={() => setLonHemi(h)}
                        className={`w-12 min-h-[56px] rounded-lg border-2 font-bold transition-all ${
                          lonHemi === h
                            ? 'bg-cyan-600 border-cyan-700 text-white ring-4 ring-cyan-300'
                            : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {h}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Reason for manual entry */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Reason for manual entry <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {MANUAL_REASONS.map(r => (
                    <button
                      key={r.value}
                      onClick={() => setManualReason(r.value)}
                      className={`
                        min-h-[56px] px-3 rounded-lg border-2 transition-all
                        text-sm font-medium
                        ${manualReason === r.value
                          ? 'bg-cyan-600 border-cyan-700 text-white ring-4 ring-cyan-300'
                          : 'bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200'
                        }
                      `}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes (optional) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Notes (optional)
                </label>
                <textarea
                  value={manualNotes}
                  onChange={(e) => setManualNotes(e.target.value)}
                  placeholder="Additional details about the position..."
                  rows={2}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none text-base"
                />
              </div>

              {/* Back button */}
              <button
                onClick={() => setShowManual(false)}
                className="w-full py-2 text-sm text-slate-500 hover:text-slate-700"
              >
                ← Back to GPS
              </button>
            </>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 p-4 border-t border-slate-200 bg-slate-50">
          <button
            onClick={onClose}
            className="flex-1 py-3 px-4 min-h-[56px] border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-100 transition-colors"
          >
            Cancel
          </button>
          {showManual && (
            <button
              onClick={handleManualSubmit}
              disabled={!canSubmitManual}
              className="flex-1 py-3 px-4 min-h-[56px] bg-cyan-600 text-white rounded-lg font-medium hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Log Position
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PositionInputModal;
